import { cloneDeep, isEqual, isPlainObject } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { setSnackbar } from '../../../redux/actions';

import { getClass } from '../../../utils/component-helpers';
import { dateService } from '../../../services/date';
import { camelCaseToDash, handleImageError } from '../../base/common/utils';
import { session } from '../../../services/session';

import AuctionParticipant from '../../base/smartlinks/auction-participant';
import BasicDropdown from '../../base/dropdowns/basic-dropdown';

import editPen from '../../../images/icons/edit-pen.png';
import linkBidPage from '../../../images/icons/link-bid.png';
import linkMatchPage from '../../../images/icons/link-match.png';
import linkoutIcon from '../../../images/icons/black-linkout.png';
import Tooltip from '../../misc/tooltip';

import './smartlink-editor.scss';
import { withOvermind } from '../../../containers/overmind/hocs/withOvermind';

type MyProps = {
  overmind: any;
  smartlinkId: any;
  match: any;
  modalView: any;
  toggleModal: any;
  setSnackbar: any;
};
type MyState = {
  auctionId: any;
  channels: any;
  imageUrl: any;
  editUrl: any;
  editingStory: any;
  loading: any;
  smartlink: any;
  winnerProductIds: any;
  inputError: any;
  updatedIndicator: any; // Highlight datetime updated when we save changes
  productIdxWithError: any;
  snackbar?: any;
  userCancel?: any;
  editName?: any;
  channelLabel?: any;
  saveMsg?: any;
};

class SmartlinkEditor extends React.Component<MyProps, MyState> {
  DateService: any;

  smartlinkId: any;

  smartlinkDetails: any;

  publisherName: any;

  initialData: any;

  smartlinkNode: any;

  displayMsgs: any;

  timeout: any;

  constructor(props) {
    super(props);

    this.DateService = dateService;

    // Smartlink editor is accessible via modal or route, check for prop to initialize via modal
    this.smartlinkId = this.props.smartlinkId || this.props.match.params.smartlinkId;

    this.smartlinkDetails = {};
    this.publisherName = null;
    this.initialData = null; // Used to detect user changes
    this.smartlinkNode = null;

    this.displayMsgs = {
      saved: 'Link edits saved.',
      priceInputError: 'Please enter a valid price format $xx.xx.',
      expirationInputError: 'Please enter a valid date format mm/dd/yyyy.',
    };

    this.state = {
      auctionId: null,
      channels: this.props.overmind.state.smartLink.channelFields,
      imageUrl: null,
      editUrl: null,
      editingStory: false,
      loading: true,
      smartlink: {},
      winnerProductIds: [],
      inputError: false,
      updatedIndicator: false, // Highlight datetime updated when we save changes
      productIdxWithError: {
        price: [],
        priceExpiration: [],
        stockExpiration: [],
      },
      snackbar: {
        saved: false,
        priceInputError: false,
        expirationInputError: false,
        serverError: false,
        editNameError: false,
      },
      userCancel: false,
    };

    this.timeout = null;
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
  }

  componentDidMount() {
    if (this.props.modalView) {
      window.addEventListener('click', this.handleOutsideClick);
    }
    this.getSmartlinkData();
  }

  componentWillUnmount() {
    if (this.props.modalView) {
      window.removeEventListener('click', this.handleOutsideClick);
    }
    if (this.timeout) {
      clearTimeout(this.timeout);
      this.timeout = null;
    }
  }

  handleOutsideClick(e) {
    if (
      this.smartlinkNode &&
      !this.smartlinkNode.contains(e.target) &&
      (!e.target.alt || e.target.alt !== 'Edit Link') &&
      !e.target.className.match('-override-btn') &&
      !e.target.className.match('remove-override') &&
      !e.target.className.match('edit-pen')
    ) {
      this.getConfirmationDialogue();
    }
  }

  getConfirmationDialogue() {
    if (this.isSmartlinkUnchanged()) {
      // Close modal if no changes
      this.props.toggleModal();
    } else {
      // Open confirmation dialogue if there are changes
      this.setState({ userCancel: true });
    }
  }

  getSmartlinkData() {
    this.props.overmind.actions.smartLink.querySmartlinkSummary(this.smartlinkId).then(data => {
      const merchantProducts = this.formatProducts(data.merchant_products);

      if (data.edit.channel) {
        data.edit.channel =
          data.edit.channel.toLowerCase() === 'all channels' ? null : data.edit.channel;
      }

      this.setSummary(data, merchantProducts);
      this.setChannel(data.edit.channel);
      this.setState(
        {
          auctionId: data.auction_id,
          editName: data.edit.name === null ? '' : data.edit.name,
          smartlink: {
            datetime_created: data.datetime_created,
            datetime_updated: data.datetime_updated,
            originalEditName: data.edit.name,
            products: merchantProducts,
          },
        },
        () => {
          this.initialData = this.formatUpdateData();
          this.setAuctionPreviewWinner();
        },
      );
    });
  }

  isSmartlinkUnchanged() {
    const updateData = this.formatUpdateData();
    return isEqual(updateData, this.initialData);
  }

  formatUpdateData() {
    const merchantProducts = cloneDeep(this.state.smartlink.products).map(product => {
      const auctionConfig = this.getAuctionConfigProps(product);
      return {
        merchant_product_id: product.merchant_product_id,
        auction_config: auctionConfig,
      };
    });

    const channel = this.state.channelLabel === 'Select Channel' ? null : this.state.channelLabel;

    return {
      merchant_products: merchantProducts,
      edit: {
        name: this.state.editName === '' ? null : this.state.editName,
        channel,
        url: this.state.editUrl,
      },
    };
  }

  setSummary(data, mps) {
    const [productImageUrl, productName, productBrand] = this.getProductInfo(mps);
    this.setState({
      imageUrl: productImageUrl,
      editUrl: data.edit.url,
    });

    this.publisherName = data.publisher.full_name;
    const brandName = productBrand ? `${productBrand} |` : '';

    this.smartlinkDetails = {
      productName: {
        label: 'Product Name',
        value: `${brandName} ${productName}`,
        linkout: data.url,
        linkoutIcon,
      },
      createdDate: {
        label: 'Created Date',
        value: this.DateService.formatDate(data.datetime_created, 'MM/DD/YY'),
      },
      smartlinkId: {
        label: 'Link ID',
        value: data.bam_link_id,
        redirect: () => this.redirectPage('match-page', data.bam_link_id),
        linkoutIcon: linkMatchPage,
      },
      auctionId: {
        label: 'Auction ID',
        value: data.auction_id,
        redirect: () => this.redirectPage('bid-page', data.bam_link_id),
        linkoutIcon: linkBidPage,
      },
    };
  }

  async updateSmartlink() {
    let error: any = null;
    const keys = Object.keys(this.state.productIdxWithError);
    for (let i = 0; i < keys.length; i++) {
      if (this.state.productIdxWithError[keys[i]].length > 0) {
        error = keys[i];
        break;
      }
    }

    if (this.state.inputError || error) {
      let type = 'priceInputError';
      if (error && (error === 'priceExpiration' || error === 'stockExpiration')) {
        type = 'expirationInputError';
      }
      this.setSnackbarMsg(type);
      return;
    }

    const smartlinkId = this.smartlinkDetails.smartlinkId.value;
    const data = this.formatUpdateData();

    const validEditName = this.validEditName(data.edit.name);
    if (!validEditName) return;

    this.props.overmind.actions.smartLink.updateSmartlinkSummary({ smartlinkId, data }).then(
      () => {
        this.setSnackbarMsg('saved');

        if (this.props.modalView) {
          this.props.toggleModal();
        } else {
          this.getSmartlinkData();
          this.setState({ updatedIndicator: true });
          this.timeout = setTimeout(() => {
            this.setState({ updatedIndicator: false });
          }, 4000);
        }
      },
      errorResp => {
        this.props.setSnackbar(errorResp.message);
      },
    );
  }

  validEditName(editName) {
    let errorMsg = '';
    if (editName && editName.length > 100) {
      errorMsg = 'Edit name cannot be more than 100 characters.';
    } else if (!editName && this.state.smartlink.originalEditName) {
      errorMsg = 'Please enter an edit name.';
    }

    if (errorMsg) {
      this.props.setSnackbar(errorMsg);
      return false;
    }

    return true;
  }

  // Auction preview functions
  setAuctionPreviewWinner() {
    this.props.overmind.actions.smartLink
      .queryAuctionPreview(this.state.auctionId)
      .then(auctionPreviewInfo => {
        const auctionParticipants = auctionPreviewInfo.initial_participants;
        const bids = auctionPreviewInfo.preview_result.bidders;
        this.setWinnerMerchantProductIds(auctionParticipants, bids);
      });
  }

  setWinnerMerchantProductIds(auctionData, bidderInfo) {
    const bids = this.mapBids(bidderInfo);
    const winnerMerchIds = this.getWinningBidMerchIds(bids);
    const winnerMerchantProductIds = winnerMerchIds.map(id => {
      return auctionData[id].merchant_product.merchant_product_id;
    });
    const sortedProducts = this.sortMerchantProducts(
      cloneDeep(this.state.smartlink.products),
      winnerMerchantProductIds,
    );

    this.setState({
      loading: false,
      smartlink: {
        ...this.state.smartlink,
        products: sortedProducts,
      },
      winnerProductIds: winnerMerchantProductIds,
    });
  }

  mapBids(bidders) {
    const bids = {};
    Object.keys(bidders).forEach(merchId => {
      bids[merchId] = bidders[merchId].current_bid;
    });
    return bids;
  }

  getWinningBidMerchIds(bids) {
    const highestBidValue = Math.max(...((Object.values(bids) as unknown) as number[]));
    const highestBidMerchIds = Object.keys(bids).filter(a => {
      return bids[a] === highestBidValue;
    });
    return highestBidMerchIds;
  }

  getAuctionConfigProps(product) {
    const rawConfig = product.hasOwnProperty('auction_config') ? product.auction_config : null;

    return {
      sale_price: {
        value:
          rawConfig.sale_price.value !== null
            ? parseFloat(rawConfig.sale_price.value).toFixed(2)
            : null,
        ttl_expiration: rawConfig.sale_price.ttl_expiration
          ? this.DateService.toIso8601UtcDatetime(rawConfig.sale_price.ttl_expiration)
          : null,
      },
      is_in_stock: {
        value: rawConfig.is_in_stock.value,
        ttl_expiration: rawConfig.is_in_stock.ttl_expiration
          ? this.DateService.toIso8601UtcDatetime(rawConfig.is_in_stock.ttl_expiration)
          : null,
      },
      participate_in_auction: {
        value: rawConfig.participate_in_auction.value,
        ttl_expiration: rawConfig.participate_in_auction.ttl_expiration
          ? this.DateService.toIso8601UtcDatetime(rawConfig.participate_in_auction.ttl_expiration)
          : null,
      },
    };
  }

  updateProduct(product, productIndex, auctionClassName = null) {
    const productsCopy = cloneDeep(this.state.smartlink.products);
    productsCopy[productIndex] = product;

    // Only execute when auction status changes
    if (auctionClassName) {
      if (auctionClassName === 'exclusive') {
        productsCopy.map(product => (product.auctionClassName = 'exclusive-disabled'));
      }
      productsCopy[productIndex].auctionClassName = auctionClassName;

      // If no exclusive products, reset auction status
      if (!this.hasExclusiveProduct(productsCopy)) {
        productsCopy.map(product => {
          product.auctionClassName = this.getAuctionClassName(product.auction_config);
          return product;
        });
      }
    }

    this.setState({
      smartlink: {
        ...this.state.smartlink,
        products: productsCopy,
      },
    });
  }

  // Channel dropdown helpers
  selectChannel(index) {
    // Need to make deep copy to prevent mutating state
    const items = this.state.channels.map(item => ({ ...item }));

    // Reset all items
    items.map(item => {
      item.selected = false;
      return item;
    });

    items[index].selected = true;

    this.setState({
      channels: items,
      channelLabel: items[index].label,
    });
  }

  setChannel(editChannel) {
    this.setState({ saveMsg: null });
    const channels = this.props.overmind.state.smartLink.channelFields;

    if (editChannel) {
      channels.map(channel => {
        channel.selected = channel.label === editChannel;
        return channel;
      });
    } else {
      editChannel = 'Select Channel';
    }

    this.setState({
      channels,
      channelLabel: editChannel,
    });
  }

  // Utils
  redirectPage(page, id) {
    const pageMap = {
      'bid-page': `/#/admin/smartlinks/${id}`,
      'match-page': `/#/admin/product-match/smartlink-match/${id}`,
    };

    window.location.assign(pageMap[page]);
  }

  getProductInfo(merchProducts) {
    const productWithImage = merchProducts.find(product => {
      return !!product.item_data.image_url;
    });
    const productWithName = merchProducts.find(product => {
      return !!product.item_data.name;
    });
    const productWithBrand = merchProducts.find(product => {
      return !!product.brand;
    });

    const image = productWithImage ? productWithImage.item_data.image_url : null;
    const name = productWithName ? productWithName.item_data.name : null;
    const brand = productWithBrand ? productWithBrand.brand : null;

    return [image, name, brand];
  }

  getAuctionClassName(auctionConfig) {
    if (auctionConfig.participate_in_auction.value === null) {
      return 'active';
    }
    return auctionConfig.participate_in_auction.value ? 'exclusive' : 'disabled';
  }

  formatProducts(products) {
    let exclusiveIdx = null;

    // Format auction config to new format
    products.map((product, idx) => {
      if (
        !product.hasOwnProperty('auction_config') ||
        !product.auction_config ||
        (product.auction_config &&
          (!product.auction_config.hasOwnProperty('participate_in_auction') ||
            !product.auction_config.hasOwnProperty('sale_price') ||
            !product.auction_config.hasOwnProperty('is_in_stock')))
      ) {
        product.auction_config = this.props.overmind.state.smartLink.auctionConfigFormat;
        // Old auction config format
      } else if (!isPlainObject(product.auction_config.participate_in_auction)) {
        product.auction_config = {
          participate_in_auction: {
            value: product.auction_config.participate_in_auction,
            ttl_expiration: null,
          },
          sale_price: {
            value: product.auction_config.sale_price,
            ttl_expiration: null,
          },
          is_in_stock: {
            value: product.auction_config.is_in_stock,
            ttl_expiration: null,
          },
        };
      }

      // Assign auction status class names
      product.auctionClassName = this.getAuctionClassName(product.auction_config);
      if (product.auctionClassName === 'exclusive') exclusiveIdx = idx;
      return product;
    });

    // Set exclusive disabled class if there's an exclusive product in the list
    if (exclusiveIdx !== null) {
      products.map((product, idx) => {
        if (exclusiveIdx === idx) return product;
        product.auctionClassName = 'exclusive-disabled';
        return product;
      });
    }

    return products;
  }

  hasExclusiveProduct(products) {
    return products.find(product => {
      return product.auctionClassName === 'exclusive';
    });
  }

  focusEditInput() {
    (document.getElementsByClassName('edit-name-input')[0] as HTMLElement).focus();
  }

  setSnackbarMsg(errorKey) {
    const msg = this.displayMsgs[errorKey];
    this.props.setSnackbar(msg);
  }

  setError(value, productIdx, key, action = '') {
    let indices = cloneDeep(this.state.productIdxWithError[key]);

    switch (action) {
      case 'add':
        indices.push(productIdx);
        break;
      case 'remove':
        indices = indices.filter(item => item !== productIdx);
        break;
      default:
        break;
    }

    this.setState(currentState => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      currentState.inputError = value;
      currentState.productIdxWithError[key] = indices;
      return currentState;
    });
  }

  addTooltip(el, key) {
    const textMap = {
      smartlinkId: 'Go to Product Match',
      auctionId: 'Go to Bid Page',
    };

    return new Tooltip(el, { text: textMap[key], size: 3, fixedSize: null, noTail: null });
  }

  sortMerchantProducts(merchantProducts, winnerIds) {
    // Sorts products by auction winners and then alphabetically, filters out of network merchants out
    const winningIds = winnerIds || this.state.winnerProductIds;
    return merchantProducts
      .filter(product => product.is_in_network && product.merchant.is_active)
      .sort((a, b) => {
        if (winningIds.includes(a.merchant_product_id)) {
          if (winningIds.includes(b.merchant_product_id)) {
            return a.merchant.full_name > b.merchant.full_name ? 1 : -1;
          }
          return -1;
        }
        if (winningIds.includes(b.merchant_product_id)) {
          return 1;
        }
        return a.merchant.full_name > b.merchant.full_name ? 1 : -1;
      });
  }

  render() {
    if (this.state.loading) return null;

    const datetimeCreated = this.DateService.formatDate(
      this.state.smartlink.datetime_created,
      'MM/DD/YY, hh:mm A',
    );
    const datetimeUpdated = this.DateService.formatDate(
      this.state.smartlink.datetime_updated,
      'MM/DD/YY, hh:mm A',
    );

    const header = !this.props.modalView ? (
      <>
        <p className={getClass('last-updated', { saved: this.state.updatedIndicator })}>
          {`Last updated ${datetimeUpdated}`}
        </p>
        <button className="save-smartlink" onClick={this.updateSmartlink.bind(this)}>
          SAVE
        </button>
      </>
    ) : null;

    // Sorts products primarily by auction winners, secondary sorted alphabetically
    const participantConfigs = this.state.smartlink.products.map((product, idx) => {
      const updateProduct = (product, idx, auctionClassName) =>
        this.updateProduct(product, idx, auctionClassName);
      return (
        <AuctionParticipant
          key={idx}
          productIndex={idx}
          product={product}
          winnerConfig={{
            multipleWinners: this.state.winnerProductIds.length > 1,
            winner: this.state.winnerProductIds.includes(product.merchant_product_id),
          }}
          datetimeUpdated={product.datetime_updated}
          updateProduct={updateProduct}
          productIdxWithError={this.state.productIdxWithError}
          setError={this.setError.bind(this)}
          auctionClassName={product.auctionClassName}
          overmind={this.props.overmind}
        />
      );
    });

    const displaySmartlinkDetails = Object.keys(this.smartlinkDetails).map(key => {
      if (this.props.modalView && !session.isAdminView && key !== 'productName') return null;

      const className = camelCaseToDash(key);
      const info = this.smartlinkDetails[key];
      const productLinkout = info.linkout ? (
        <a href={info.linkout} target="_blank" rel="noopener noreferrer">
          <img src={info.linkoutIcon} alt="link out" className={`link-out ${className}`} />
        </a>
      ) : null;

      const redirect = info.redirect ? (
        <img
          src={info.linkoutIcon}
          alt="link out"
          ref={node => this.addTooltip(node, key)}
          onClick={() => info.redirect()}
          className={`link-out ${className}`}
        />
      ) : null;

      return (
        <div className={`detail ${className}`} key={className}>
          <div className={`${className}-label`}>{info.label}</div>
          <div className={className}>{info.value}</div>
          {productLinkout}
          {redirect}
        </div>
      );
    });

    const editUrlLinkout = this.state.editUrl ? (
      <a href={this.state.editUrl} target="_blank" rel="noopener noreferrer">
        <img src={linkoutIcon} alt="link out" className="link-out edit-url" />
      </a>
    ) : null;

    const editPenNode = !this.state.editingStory ? (
      <img src={editPen} alt="edit" onClick={() => this.focusEditInput()} className="edit-pen" />
    ) : null;

    const datetimeUpdateNode =
      datetimeUpdated !== datetimeCreated ? (
        <p
          className={getClass('last-updated', {
            saved: this.state.updatedIndicator,
          })}
        >
          {`Updated ${datetimeUpdated}`}
        </p>
      ) : null;

    const footer = this.props.modalView ? (
      <div className="footer">
        <div className="datetimes">
          <p className="created">{`Created ${datetimeCreated}`}</p>
          {datetimeUpdateNode}
        </div>
        <div className="buttons">
          <button className="cancel-edit" onClick={() => this.getConfirmationDialogue()}>
            Cancel
          </button>
          <button className="save-smartlink" onClick={this.updateSmartlink.bind(this)}>
            Save
          </button>
        </div>
      </div>
    ) : null;

    const confirmationDialogue =
      this.props.modalView && this.state.userCancel ? (
        <div className="confirmation-dialogue">
          <div className="dialogue-content">
            <p className="header">Discard Changes?</p>
            <p className="body">If you leave now your edits will not be saved</p>
            <div className="button-container">
              <div
                className="cancel"
                onClick={e => {
                  e.stopPropagation();
                  this.setState({ userCancel: false });
                }}
              >
                Cancel
              </div>
              <div className="discard" onClick={this.props.toggleModal}>
                Discard
              </div>
            </div>
          </div>
        </div>
      ) : null;

    return (
      <div ref={node => (this.smartlinkNode = node)} className="smartlink-info-wrapper">
        <div className="smartlink-editor">
          <div className={`smartlink-data${session.isAdminView ? ' admin' : ''}`}>
            <div className="header">
              <h2>Link Details</h2>
              {header}
            </div>

            <div className="image-holder">
              <img
                src={this.state.imageUrl}
                alt="product"
                onError={e => handleImageError(e, this.state.imageUrl)}
              />
            </div>

            <div className="smartlink-container">
              <div className="publisher-name">{this.publisherName}</div>
              <div className="dot"></div>
              <BasicDropdown
                items={this.state.channels}
                selectItem={this.selectChannel.bind(this)}
                dropdownLabel={this.state.channelLabel}
              />
              <div className="detail story-name">
                <div className="story-name-label">Story Name</div>
                <div className="flex-box">
                  <textarea
                    readOnly={!this.state.editingStory}
                    rows={2}
                    className="edit-name-input"
                    value={this.state.editName}
                    placeholder={this.state.editName}
                    onFocus={() => this.setState({ editingStory: true })}
                    onBlur={() => this.setState({ editingStory: false })}
                    onChange={event => this.setState({ editName: event.target.value })}
                  />
                  <div className="flex-box edit-icons">
                    {editPenNode}
                    {editUrlLinkout}
                  </div>
                </div>
              </div>
              {displaySmartlinkDetails}
            </div>
          </div>
          <h2 className="matched-products-header">Matched Products</h2>
          <div className="auction-participants">{participantConfigs}</div>
        </div>
        {footer}
        {confirmationDialogue}
      </div>
    );
  }
}

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const mapDispatchToProps = dispatch => {
  return {
    setSnackbar: msg => dispatch(setSnackbar(msg)),
  };
};

// eslint-disable-next-line import/no-default-export,@typescript-eslint/ban-ts-comment
// @ts-ignore
export default connect(null, mapDispatchToProps)(withOvermind(SmartlinkEditor));
